import AtriumApi from "api";

/**
 * Builds the API client for one assessment audience. The staff and volunteer
 * modules expose the same endpoints under different prefixes
 * (`/v2/staff/*` and `/v2/volunteers/*`); every method resolves to the
 * response body (`{ success, data, … }`) and rethrows request errors.
 *
 * @param {"staff"|"volunteers"} prefix Path segment after `/v2/`.
 */
export const makeAssessmentService = (prefix) => {
  const base = `/v2/${prefix}`;
  const get = (path, params) => AtriumApi.get(`${base}${path}`, params && { params }).then((r) => r.data);
  const post = (path, body) => AtriumApi.post(`${base}${path}`, body).then((r) => r.data);
  const put = (path, body) => AtriumApi.put(`${base}${path}`, body).then((r) => r.data);
  const del = (path) => AtriumApi.delete(`${base}${path}`).then((r) => r.data);

  return {
    // Dashboard
    getOverview: (params = {}) => get("/dashboard/overview", params),
    getAssessmentOverview: (params = {}) => get("/dashboard/assessment-overview", params),
    getHealthTrend: (params = {}) => get("/dashboard/health-trend", params),
    getRoleFitDistribution: (params = {}) => get("/dashboard/role-fit-distribution", params),
    getStyleDistribution: (params = {}) => get("/dashboard/style-distribution", params),
    getAtRiskUsers: (params = {}) => get("/dashboard/at-risk", params),
    getUtilizationInsights: (params = {}) => get("/dashboard/utilization-insights", params),
    getMinistryGaps: (params = {}) => get("/dashboard/ministry-gaps", params),
    getTeamDynamics: (params = {}) => get("/dashboard/team-dynamics", params),
    recalculateUtilization: () => post("/dashboard/recalculate-utilization"),

    // Profiles
    getProfiles: (params = {}) => get("/profiles", params),
    getProfileStats: () => get("/profiles/stats"),
    getUserProfile: (userId) => get(`/profiles/${userId}`),
    updateProfileFlags: (userId, flags) => put(`/profiles/${userId}/flags`, flags),
    regenerateProfileSummary: (userId) => post(`/profiles/${userId}/regenerate-summary`),
    calculateRoleMatches: (userId) => post(`/profiles/${userId}/calculate-matches`),
    getUserRoleMatches: (userId) => get(`/profiles/${userId}/role-matches`),

    // Role profiles
    getRoleProfiles: (params = {}) => get("/profiles/roles", params),
    createRoleProfile: (data) => post("/profiles/roles", data),
    updateRoleProfile: (id, data) => put(`/profiles/roles/${id}`, data),
    getRoleCandidates: (roleId, params = {}) => get(`/profiles/roles/${roleId}/candidates`, params),
    generateRoleRecommendation: (roleId, userId) => post(`/profiles/roles/${roleId}/recommend/${userId}`),

    // Pulse surveys and weekly check-ins
    getSurveys: (params = {}) => get("/pulse/surveys", params),
    getSurvey: (id) => get(`/pulse/surveys/${id}`),
    createSurvey: (data) => post("/pulse/surveys", data),
    updateSurvey: (id, data) => put(`/pulse/surveys/${id}`, data),
    sendSurvey: (id, options = {}) => post(`/pulse/surveys/${id}/send`, options),
    getSurveyResponses: (id, params = {}) => get(`/pulse/surveys/${id}/responses`, params),
    getCareTasks: (params = {}) => get("/pulse/care", params),
    addressCareTask: (responseId, data) => put(`/pulse/care/${responseId}/address`, data),
    sendFollowUp: (responseId, data) => post(`/pulse/responses/${responseId}/follow-up`, data),
    getUserPulseHistory: (userId, params = {}) => get(`/pulse/user/${userId}/history`, params),
    analyzeResponse: (responseId) => post(`/pulse/responses/${responseId}/analyze`),
    getCheckInOverview: (params = {}) => get("/pulse/checkin-overview", params),
    getOrganizationSummary: (params = {}) => get("/pulse/summary", params),
    sendCheckInNow: () => post("/pulse/send-now"),
    getDigestRecipients: () => get("/pulse/digest/recipients"),
    sendDigestNow: (adminIds = []) => post("/pulse/digest/send", { adminIds }),

    // Pulse questions
    getPulseQuestions: (params = {}) => get("/pulse/questions", params),
    getPulseQuestion: (id) => get(`/pulse/questions/${id}`),
    createPulseQuestion: (data) => post("/pulse/questions", data),
    updatePulseQuestion: (id, data) => put(`/pulse/questions/${id}`, data),
    deletePulseQuestion: (id) => del(`/pulse/questions/${id}`),
    reorderPulseQuestions: (orderedIds) => post("/pulse/questions/reorder", { orderedIds }),
    copyDefaultPulseQuestions: () => post("/pulse/questions/copy-defaults"),

    // Intake forms
    getIntakeForms: (params = {}) => get("/intake/forms", params),
    getIntakeForm: (id) => get(`/intake/forms/${id}`),
    createIntakeForm: (data) => post("/intake/forms", data),
    updateIntakeForm: (id, data) => put(`/intake/forms/${id}`, data),
    deleteIntakeForm: (id) => del(`/intake/forms/${id}`),
    getFunQuestions: () => get("/intake/questions"),
    createFunQuestion: (data) => post("/intake/questions", data),
    getAssessmentFrameworks: () => get("/intake/frameworks"),
    getSubmissions: (params = {}) => get("/intake/submissions", params),
    getEligibleUsers: (formId) => get(`/intake/forms/${formId}/eligible-users`),
    sendAssessment: (formId, userIds, deliveryMethod = "email") =>
      post(`/intake/forms/${formId}/send`, { userIds, deliveryMethod }),
    getIntakeFormByToken: (token) => get(`/public/intake/token/${token}`),

    // Public intake and pulse (no auth; org comes from the subdomain)
    getPublicIntakeForm: (slug) => get(`/public/intake/${slug}`),
    startIntakeSubmission: (slug, data) => post(`/public/intake/${slug}/start`, data),
    saveProfile: (token, profileData) => {
      // Axios sets the multipart boundary itself; avatarPreview is local UI state.
      const formData = new FormData();
      Object.entries(profileData).forEach(([key, value]) => {
        if (key === "avatarPreview") return;
        if (key === "avatar" && value instanceof File) {
          formData.append("avatar", value);
        } else if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });
      return post(`/public/submission/${token}/profile`, formData);
    },
    saveFunResponses: (token, responses) => post(`/public/submission/${token}/fun-responses`, { responses }),
    saveAssessmentResponses: (token, frameworkId, responses) =>
      post(`/public/submission/${token}/assessment`, { frameworkId, responses }),
    completeIntakeSubmission: (token, email) => post(`/public/submission/${token}/complete`, { email }),
    getPublicPulse: (token) => get(`/public/pulse/${token}`),
    submitPulse: (token, data) => post(`/public/pulse/${token}/respond`, data),
  };
};

export default makeAssessmentService;
